import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import apiService from "../services/api";
import TutorRateManager from "../components/tutor-rates/TutorRateManager";
import ConfirmModal from "../components/ConfirmModal";
import { formatCurrency } from "../utils/currencyUtils";
import { useAuth } from "../contexts/AuthContext";

const TutorCard = ({ tutor, styles, onManage, isSuperAdmin }) => {
  const queryClient = useQueryClient();
  const [confirmExclude, setConfirmExclude] = useState(null); // null | true | false
  const [confirmDormant, setConfirmDormant] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [employeeBasePay, setEmployeeBasePay] = useState("");
  const fullName =
    `${tutor.firstName || ""} ${tutor.lastName || ""}`.trim() || "Unnamed Tutor";

  const rateDisplay = tutor.currentRate
    ? formatCurrency(tutor.currentRate.hourlyRate)
    : "Default";

  const rateStyle = tutor.currentRate
    ? { fontWeight: "500", color: "#166534", fontSize: "0.875rem" }
    : { fontWeight: "500", color: "#9ca3af", fontStyle: "italic", fontSize: "0.875rem" };

  const { data: payrollSettings } = useQuery({
    queryKey: ["tutorPayrollSettings", tutor.canonicalTutorId],
    queryFn: () => apiService.getTutorPayrollSettings(tutor.canonicalTutorId),
    enabled: !!tutor.canonicalTutorId,
    staleTime: 5 * 60 * 1000,
  });

  const toggleMutation = useMutation({
    mutationFn: (excludeFromBonuses) =>
      apiService.updateTutorPayrollSettings(tutor.canonicalTutorId, excludeFromBonuses),
    onSuccess: () => {
      queryClient.invalidateQueries(["tutorPayrollSettings", tutor.canonicalTutorId]);
      setConfirmExclude(null);
    },
  });

  const employeeMutation = useMutation({
    mutationFn: ({ isEmployee, monthlyBasePay }) =>
      apiService.updateTutorEmployeeStatus(tutor.canonicalTutorId, isEmployee, monthlyBasePay),
    onSuccess: () => {
      queryClient.invalidateQueries(["tutorPayrollSettings", tutor.canonicalTutorId]);
      setShowEmployeeModal(false);
      setEmployeeBasePay("");
    },
  });

  const dormantMutation = useMutation({
    mutationFn: (dormant) => apiService.setTutorDormant(tutor.canonicalTutorId, dormant),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tutors"] });
      setConfirmDormant(false);
    },
  });

  const excludeFromBonuses = payrollSettings?.excludeFromBonuses ?? false;
  const isEmployee = payrollSettings?.isEmployee ?? false;
  const monthlyBasePay = payrollSettings?.monthlyBasePay;

  return (
    <div
      style={styles.tutorCard}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = styles.tutorCardHover.transform;
        e.currentTarget.style.boxShadow = styles.tutorCardHover.boxShadow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = styles.tutorCard.boxShadow;
      }}
    >
      <div style={styles.tutorHeader}>
        <h3 style={styles.tutorName}>{fullName}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginTop: "0.375rem" }}>
          {isEmployee && (
            <span style={{
              padding: "2px 10px", borderRadius: "12px", fontSize: "0.75rem",
              fontWeight: "600", backgroundColor: "#dbeafe", color: "#1e40af",
            }}>
              Employee{isSuperAdmin && monthlyBasePay != null ? ` · ${formatCurrency(monthlyBasePay)}/mo` : ""}
            </span>
          )}
          {excludeFromBonuses && (
            <span style={{
              padding: "2px 10px", borderRadius: "12px", fontSize: "0.75rem",
              fontWeight: "600", backgroundColor: "#fee2e2", color: "#991b1b",
            }}>
              Excluded from mentor and counselor bonuses
            </span>
          )}
        </div>
      </div>

      <div style={styles.tutorInfo}>
        <div style={styles.tutorInfoItem}>
          <span style={styles.tutorInfoLabel}>Email:</span>
          <span style={styles.tutorInfoValue}>{tutor.email || "Not available"}</span>
        </div>
        <div style={styles.tutorInfoItem}>
          <span style={styles.tutorInfoLabel}>Status:</span>
          <span style={styles.tutorInfoValue}>
            {tutor.active !== false ? "Active" : "Inactive"}
          </span>
        </div>
        {tutor.timezone && (
          <div style={styles.tutorInfoItem}>
            <span style={styles.tutorInfoLabel}>Timezone:</span>
            <span style={styles.tutorInfoValue}>{tutor.timezone}</span>
          </div>
        )}
        <div style={styles.tutorInfoItem}>
          <span style={styles.tutorInfoLabel}>Current Rate:</span>
          <span style={rateStyle}>{rateDisplay}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setConfirmExclude(!excludeFromBonuses); }}
          disabled={toggleMutation.isPending}
          onMouseEnter={(e) => { e.target.style.backgroundColor = excludeFromBonuses ? "#15803d" : "#b45309"; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = excludeFromBonuses ? "#16a34a" : "#d97706"; }}
          style={{
            width: "100%",
            padding: "0.5rem 1rem",
            backgroundColor: excludeFromBonuses ? "#16a34a" : "#d97706",
            color: "white",
            border: "none",
            borderRadius: "0.375rem",
            fontSize: "0.875rem",
            fontWeight: "500",
            cursor: toggleMutation.isPending ? "default" : "pointer",
            transition: "background-color 0.2s",
            opacity: toggleMutation.isPending ? 0.6 : 1,
          }}
        >
          {excludeFromBonuses ? "Remove Bonus Exclusion" : "Exclude from Bonuses"}
        </button>
      </div>

      {confirmExclude !== null && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setConfirmExclude(null)}
        >
          <div
            style={{
              backgroundColor: "white", borderRadius: "0.5rem", padding: "1.5rem",
              maxWidth: "400px", width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 0.75rem", color: "#001272", fontSize: "1.1rem", fontWeight: "600" }}>
              {confirmExclude ? "Exclude from bonuses?" : "Remove bonus exclusion?"}
            </h3>
            <p style={{ margin: "0 0 1.25rem", fontSize: "0.875rem", color: "#4b5563" }}>
              {confirmExclude
                ? `${fullName} will no longer earn mentor or counselor bonuses.`
                : `${fullName} will be eligible for mentor and counselor bonuses again.`}
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmExclude(null)}
                style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", background: "white", cursor: "pointer", fontSize: "0.875rem" }}
              >
                Cancel
              </button>
              <button
                onClick={() => toggleMutation.mutate(confirmExclude)}
                disabled={toggleMutation.isPending}
                style={{
                  padding: "0.5rem 1rem", border: "none", borderRadius: "0.375rem",
                  backgroundColor: confirmExclude ? "#dc2626" : "#16a34a",
                  color: "white", cursor: toggleMutation.isPending ? "default" : "pointer",
                  fontSize: "0.875rem", fontWeight: "500",
                  opacity: toggleMutation.isPending ? 0.6 : 1,
                }}
              >
                {toggleMutation.isPending ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        style={styles.manageButton}
        onMouseEnter={(e) => { e.target.style.backgroundColor = styles.manageButtonHover.backgroundColor; }}
        onMouseLeave={(e) => { e.target.style.backgroundColor = styles.manageButton.backgroundColor; }}
        onClick={(e) => {
          e.stopPropagation();
          if (!tutor.canonicalTutorId) return;
          onManage(tutor.canonicalTutorId, fullName);
        }}
      >
        Manage Rates
      </button>
      <button
        onClick={() => setConfirmDormant(true)}
        disabled={dormantMutation.isPending}
        style={{
          marginTop: "0.5rem", width: "100%", padding: "0.4rem 1rem",
          backgroundColor: "transparent",
          border: `1px solid ${tutor.isDormant ? "#16a34a" : "#9ca3af"}`,
          color: tutor.isDormant ? "#16a34a" : "#6b7280",
          borderRadius: "0.375rem", fontSize: "0.8rem", fontWeight: "500",
          cursor: dormantMutation.isPending ? "default" : "pointer",
          opacity: dormantMutation.isPending ? 0.6 : 1,
        }}
      >
        {tutor.isDormant ? "Mark as Active" : "Mark as Dormant"}
      </button>

      {isSuperAdmin && (
        <button
          onClick={() => {
            setEmployeeBasePay(monthlyBasePay != null ? String(monthlyBasePay) : "");
            setShowEmployeeModal(true);
          }}
          disabled={employeeMutation.isPending}
          style={{
            marginTop: "0.5rem", width: "100%", padding: "0.4rem 1rem",
            backgroundColor: "transparent",
            border: `1px solid ${isEmployee ? "#dc2626" : "#1d4ed8"}`,
            color: isEmployee ? "#dc2626" : "#1d4ed8",
            borderRadius: "0.375rem", fontSize: "0.8rem", fontWeight: "500",
            cursor: employeeMutation.isPending ? "default" : "pointer",
            opacity: employeeMutation.isPending ? 0.6 : 1,
          }}
        >
          {isEmployee ? "Remove Employee Status" : "Mark as Employee"}
        </button>
      )}

      {showEmployeeModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowEmployeeModal(false)}
        >
          <div
            style={{
              backgroundColor: "white", borderRadius: "0.5rem", padding: "1.5rem",
              maxWidth: "420px", width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isEmployee ? (
              <>
                <h3 style={{ margin: "0 0 0.75rem", color: "#001272", fontSize: "1.1rem", fontWeight: "600" }}>
                  Remove Employee Status
                </h3>
                <p style={{ margin: "0 0 1.25rem", fontSize: "0.875rem", color: "#4b5563" }}>
                  {fullName} will no longer receive a monthly base pay line item in payroll.
                </p>
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setShowEmployeeModal(false)}
                    style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", background: "white", cursor: "pointer", fontSize: "0.875rem" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => employeeMutation.mutate({ isEmployee: false, monthlyBasePay: null })}
                    disabled={employeeMutation.isPending}
                    style={{ padding: "0.5rem 1rem", border: "none", borderRadius: "0.375rem", backgroundColor: "#dc2626", color: "white", cursor: employeeMutation.isPending ? "default" : "pointer", fontSize: "0.875rem", fontWeight: "500", opacity: employeeMutation.isPending ? 0.6 : 1 }}
                  >
                    {employeeMutation.isPending ? "Saving..." : "Confirm"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: "0 0 0.75rem", color: "#001272", fontSize: "1.1rem", fontWeight: "600" }}>
                  Mark as Employee
                </h3>
                <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", color: "#4b5563" }}>
                  {fullName} will receive a monthly base pay line item in payroll. Set the fixed monthly amount in USD.
                </p>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#374151", marginBottom: "0.375rem" }}>
                  Monthly Base Pay (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={employeeBasePay}
                  onChange={(e) => setEmployeeBasePay(e.target.value)}
                  placeholder="e.g. 5000.00"
                  style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", marginBottom: "1.25rem", boxSizing: "border-box" }}
                  autoFocus
                />
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setShowEmployeeModal(false)}
                    style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", background: "white", cursor: "pointer", fontSize: "0.875rem" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const pay = parseFloat(employeeBasePay);
                      if (!employeeBasePay || isNaN(pay) || pay <= 0) return;
                      employeeMutation.mutate({ isEmployee: true, monthlyBasePay: pay });
                    }}
                    disabled={employeeMutation.isPending || !employeeBasePay || parseFloat(employeeBasePay) <= 0}
                    style={{ padding: "0.5rem 1rem", border: "none", borderRadius: "0.375rem", backgroundColor: "#1d4ed8", color: "white", cursor: (employeeMutation.isPending || !employeeBasePay) ? "default" : "pointer", fontSize: "0.875rem", fontWeight: "500", opacity: (employeeMutation.isPending || !employeeBasePay) ? 0.6 : 1 }}
                  >
                    {employeeMutation.isPending ? "Saving..." : "Confirm"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDormant}
        title={tutor.isDormant ? "Mark as Active" : "Mark as Dormant"}
        message={
          tutor.isDormant
            ? `Mark ${fullName} as active? They will appear in the tutor list again.`
            : `Mark ${fullName} as dormant? They will be hidden from the tutor list by default.`
        }
        confirmLabel={tutor.isDormant ? "Mark as Active" : "Mark as Dormant"}
        confirmStyle={tutor.isDormant ? "primary" : "danger"}
        onConfirm={() => dormantMutation.mutate(!tutor.isDormant)}
        onCancel={() => setConfirmDormant(false)}
      />
    </div>
  );
};

const TutorRatesPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [selectedTutorId, setSelectedTutorId] = useState(null);
  const [selectedTutorName, setSelectedTutorName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDormant, setShowDormant] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 500;

  // Fetch tutors
  const {
    data: tutorsData,
    isLoading: isLoadingTutors,
    error: tutorsError,
  } = useQuery({
    queryKey: ["tutors", currentPage, pageSize],
    queryFn: () => apiService.getTutors(currentPage, pageSize),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle different API response structures
  let tutors = [];
  let totalPages = 0;
  let totalElements = 0;

  if (tutorsData) {
    if (Array.isArray(tutorsData)) {
      // API returned a simple array
      tutors = tutorsData;
      totalElements = tutorsData.length;
    } else if (tutorsData.content && Array.isArray(tutorsData.content)) {
      // API returned paginated response
      tutors = tutorsData.content;
      totalPages = tutorsData.totalPages || 0;
      totalElements = tutorsData.totalElements || tutorsData.content.length;
    } else if (Array.isArray(tutorsData.tutors)) {
      // API returned object with tutors array
      tutors = tutorsData.tutors;
      totalElements = tutorsData.tutors.length;
    }
  }

  // Filter tutors based on dormant flag and search term
  const filteredTutors = tutors.filter((tutor) => {
    if (!showDormant && tutor.isDormant) return false;
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${tutor.firstName || ""} ${tutor.lastName || ""}`.toLowerCase();
    const email = (tutor.email || "").toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const handleOpenRateModal = (tutorId, tutorName) => {
    if (!tutorId) {
      return;
    }
    setSelectedTutorId(tutorId);
    setSelectedTutorName(tutorName);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTutorId(null);
    setSelectedTutorName("");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const styles = {
    page: {
      padding: "1rem 0",
    },
    pageHeader: {
      marginBottom: "2rem",
    },
    pageHeaderH1: {
      fontSize: "1.875rem",
      fontWeight: "700",
      color: "#001272",
      marginBottom: "0.5rem",
    },
    pageHeaderP: {
      color: "#001272",
      opacity: "0.8",
      fontSize: "1rem",
    },
    controls: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1.5rem",
      flexWrap: "wrap",
      gap: "1rem",
    },
    searchInput: {
      padding: "0.5rem 1rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      minWidth: "300px",
      outline: "none",
      transition: "border-color 0.2s",
    },
    searchInputFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "4rem 0",
    },
    spinner: {
      border: "3px solid #f3f3f3",
      borderTop: "3px solid #53bdeb",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite",
      marginBottom: "1rem",
    },
    errorContainer: {
      background: "#fee2e2",
      border: "1px solid #fca5a5",
      borderRadius: "0.375rem",
      padding: "1rem",
      marginBottom: "1.5rem",
    },
    errorText: {
      color: "#991b1b",
      margin: "0",
    },
    tutorsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    tutorCard: {
      background: "white",
      borderRadius: "0.5rem",
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      padding: "1.5rem",
      border: "1px solid #f1f5f9",
      transition: "transform 0.2s, box-shadow 0.2s",
      cursor: "pointer",
    },
    tutorCardHover: {
      transform: "translateY(-2px)",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    },
    tutorHeader: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      marginBottom: "1rem",
    },
    tutorName: {
      fontSize: "1.125rem",
      fontWeight: "600",
      color: "#001272",
      margin: "0",
    },

    tutorInfo: {
      marginBottom: "1rem",
    },
    tutorInfoItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0.5rem 0",
      borderBottom: "1px solid #f1f5f9",
    },
    tutorInfoLabel: {
      color: "#001272",
      opacity: "0.8",
      fontSize: "0.875rem",
    },
    tutorInfoValue: {
      fontWeight: "500",
      color: "#001272",
      fontSize: "0.875rem",
    },
    manageButton: {
      width: "100%",
      padding: "0.5rem 1rem",
      backgroundColor: "#001272",
      color: "white",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    manageButtonHover: {
      backgroundColor: "#000d5c",
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "1rem",
      marginTop: "2rem",
    },
    paginationButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#f3f4f6",
      color: "#374151",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    paginationButtonDisabled: {
      opacity: "0.5",
      cursor: "not-allowed",
    },
    paginationButtonHover: {
      backgroundColor: "#e5e7eb",
    },
    paginationInfo: {
      color: "#6b7280",
      fontSize: "0.875rem",
    },
    emptyState: {
      textAlign: "center",
      padding: "3rem 1rem",
      color: "#6b7280",
    },
    emptyStateTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      marginBottom: "0.5rem",
    },
    emptyStateText: {
      fontSize: "0.875rem",
    },
  };

  if (isLoadingTutors) {
    return (
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageHeaderH1}>Rates</h1>
          <p style={styles.pageHeaderP}>Manage hourly rates for tutors</p>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={{ color: "#001272" }}>Loading tutors...</p>
        </div>
      </div>
    );
  }

  if (tutorsError) {
    return (
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageHeaderH1}>Rates</h1>
          <p style={styles.pageHeaderP}>Manage hourly rates for tutors</p>
        </div>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>
            Error loading tutors: {tutorsError.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageHeaderH1}>Rates</h1>
        <p style={styles.pageHeaderP}>
          Manage hourly rates for tutors. Click on a tutor to view and manage
          their rate history.
        </p>
      </div>

      <div style={styles.controls}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          onFocus={(e) => {
            e.target.style.borderColor = styles.searchInputFocus.borderColor;
            e.target.style.boxShadow = styles.searchInputFocus.boxShadow;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = styles.searchInput.borderColor;
            e.target.style.boxShadow = "";
          }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={showDormant}
            onChange={(e) => setShowDormant(e.target.checked)}
          />
          Show Dormant Tutors
        </label>
        <div style={styles.paginationInfo}>
          Showing {filteredTutors.length} of {totalElements} tutors
          {pageSize >= totalElements && totalElements > 0 && (
            <span style={{ marginLeft: "0.5rem", color: "#10b981" }}>
              (All tutors loaded)
            </span>
          )}
        </div>
      </div>

      {filteredTutors.length === 0 ? (
        <div style={styles.emptyState}>
          <h3 style={styles.emptyStateTitle}>No tutors found</h3>
          <p style={styles.emptyStateText}>
            {searchTerm
              ? "No tutors match your search criteria. Try a different search term."
              : "No tutors available. Tutors will appear here once they are synced from TutorCruncher."}
          </p>
        </div>
      ) : (
        <>
          <div style={styles.tutorsGrid}>
            {filteredTutors.map((tutor) => (
              <TutorCard
                key={tutor.canonicalTutorId}
                tutor={tutor}
                styles={styles}
                onManage={handleOpenRateModal}
                isSuperAdmin={isSuperAdmin}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={{
                  ...styles.paginationButton,
                  ...(currentPage === 0 ? styles.paginationButtonDisabled : {}),
                }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                onMouseEnter={(e) => {
                  if (currentPage > 0) {
                    e.target.style.backgroundColor =
                      styles.paginationButtonHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor =
                    styles.paginationButton.backgroundColor;
                }}
              >
                Previous
              </button>

              <span style={styles.paginationInfo}>
                Page {currentPage + 1} of {totalPages}
              </span>

              <button
                style={{
                  ...styles.paginationButton,
                  ...(currentPage >= totalPages - 1
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                onMouseEnter={(e) => {
                  if (currentPage < totalPages - 1) {
                    e.target.style.backgroundColor =
                      styles.paginationButtonHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor =
                    styles.paginationButton.backgroundColor;
                }}
              >
                Next
              </button>
            </div>
          )}

          {isModalOpen && selectedTutorId && (
            <TutorRateManager
              tutorId={selectedTutorId}
              tutorName={selectedTutorName}
              onClose={handleCloseModal}
            />
          )}
        </>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @media (max-width: 768px) {
            .tutors-grid {
              grid-template-columns: 1fr;
            }

            .controls {
              flex-direction: column;
              align-items: stretch;
            }

            .search-input {
              min-width: 100%;
            }

            .pagination {
              flex-direction: column;
              gap: 0.5rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default TutorRatesPage;
